import { Link } from 'react-router-dom';
import { Size, documentStatusPill, formatDate, serviceReportStateLabel, t, type ServiceReportState, type StatusColorGroup, type WorkOrderDocumentRow, type WorkOrderDocumentsAnswer } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Pill } from '../../components/controls/Pill';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { useApi, FILES_PATH } from './api';
import { openSheet } from './ViewPdfLink';
import { WORK_ORDERS_PATH } from './WorkOrderPanels';
import styles from './WorkOrderDocuments.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const workOrderDocumentsPath = (workOrderId: string): string => `${WORK_ORDERS_PATH}/${workOrderId}/documents`;

/** 🔒 A report's state as its pill reads it: open in amber, signed and sent in green, void in grey (decision 2910). */
const REPORT_GROUPS: Readonly<Record<ServiceReportState, StatusColorGroup>> = { Open: 'warning', Signed: 'good', Sent: 'good', Void: 'inactive' };

function statePill(row: WorkOrderDocumentRow) {
  if (!row.state) return null;
  const { text, group } = row.kind === 'ServiceReport'
    ? { text: serviceReportStateLabel(row.state as ServiceReportState), group: REPORT_GROUPS[row.state as ServiceReportState] ?? 'inactive' }
    : documentStatusPill('InvoiceStatus', row.state);
  return <span data-document-state={row.state}><Pill text={text} foreground={`var(--status-${group}-text)`} background={`var(--status-${group}-bg)`} size={Size.Small} /></span>;
}

/**
 * 🔒 The job's Documents tile (piece 29; decision 2910): each visit's Service
 * Report with its state, and every invoice raised from the job. A job with no
 * visit keeps its own sheet here. A report opens its page; a signed one prints
 * its frozen sheet, the version the lead signed.
 */
export function WorkOrderDocuments({ workOrderId, apiBaseUrl = '', onError }: { workOrderId: string; apiBaseUrl?: string; onError: (message: string) => void }) {
  const read = useApi<WorkOrderDocumentsAnswer>(workOrderDocumentsPath(workOrderId), apiBaseUrl);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const print = async (row: WorkOrderDocumentRow) => {
    const address = row.kind === 'WorkOrder'
      ? `${apiBaseUrl}${row.href ?? ''}`
      : `${apiBaseUrl}${FILES_PATH}/${row.fileId}${row.versionId ? `/versions/${row.versionId}` : ''}/stream`;
    const opened = await openSheet(address);
    if (opened.outcome !== 'opened') onError(opened.message);
  };
  return (
    <AsyncStatePanel state={read.state} emptyAction={t('serviceReport.documents.none')} onRetry={read.reload}>
      {(answer) => (
        <section className={styles.tile} data-work-order-documents={workOrderId} data-work-order-documents-count={answer.rows.length}>
          {answer.rows.length === 0 ? <p className={styles.hint} data-work-order-documents-none="true">{t('serviceReport.documents.none')}</p> : null}
          <ul className={styles.rows}>
            {answer.rows.map((row) => (
              <li key={`${row.kind}-${row.id}`} className={styles.row} data-work-order-document={row.kind} data-work-order-document-id={row.id}>
                <span className={styles.kind}>{row.kind === 'WorkOrder' ? t('serviceReport.documents.sheet') : row.kind === 'ServiceReport' ? t('documentName.serviceReport') : t('serviceReport.documents.invoice')}</span>
                {/* An in-app route opens through the router; the job's own sheet is a PDF the Print control opens. */}
                {row.href && row.kind !== 'WorkOrder' ? <Link className={styles.number} to={row.href}>{row.number}</Link> : <span className={styles.number}>{row.number}</span>}
                {statePill(row)}
                <span className={styles.hint}>{row.at ? formatDate(row.at, zone, dateFormat) : ''}</span>
                {row.kind === 'WorkOrder' || row.fileId ? (
                  <span className={styles.action} data-work-order-document-print={row.id}>
                    <Button size={Size.Small} text={[t('serviceReport.documents.print')]} actions={[() => void print(row)]} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      )}
    </AsyncStatePanel>
  );
}
